/**
 * Questions Service
 * API calls for question generation and management
 */

import axios from 'axios';
import { API_BASE_URL } from '../config/api.config';

const API_URL = API_BASE_URL;

interface GenerateQuestionsRequest {
  exam: string;
  oeCode?: string;     // CLIENT REQUIREMENT: Single OE code per question
  count: number;
  questionType: string; // Singular, not plural
  difficulty?: string;
  customPrompt?: string; // Custom prompt for prompt-based generation
  format?: 'anbima' | 'short'; // MCQ format (ANBIMA: 750-850 chars, SHORT: 30-500 chars)
}

interface Question {
  _id: string;
  stem: string;           // Question text (backend field name)
  question?: string;      // Alias for stem (legacy)
  type: string;
  difficulty: string;
  exam?: string;
  exams?: string[];
  examName?: string;      // Exam name (display name)
  oeCode: string;         // CLIENT REQUIREMENT: Single OE code per question
  course?: string;        // Course ID
  courseName?: string;    // Course name (display name)
  alternatives?: Array<{
    letter: string;       // 'A', 'B', 'C', 'D'
    text: string;
    explanation?: string;
    isCorrect: boolean;
  }>;
  correctAnswer?: string;
  explanation: string;
  status: string;
  validationScore?: number;
  cognitiveLevel?: string;
  estimatedTime?: number;
  tags?: string[];
  sourceRefs?: Array<{
    sourceId: string;
    citation?: string;
  }>;
  sourceDocuments?: Array<{
    documentId: string;
    title: string;
    filename: string;
  }>;
  // Free knowledge source (NQG-RQ-26 v2)
  sourceType?: 'standard' | 'free_pdf';
  originalPdfQuestionId?: string;
  syncedFromPdfBankAt?: string;
  sourceRef?: {
    analysisId?: string;
    topicIndex?: number;
    code?: string;
    objectiveText?: string;
  };
  cognitiveTaxonomy?: {
    framework?: 'bloom' | 'action_verb';
    value?: string;
  };
  topicWeighting?: number;
  generationMetadata?: {
    pipelineVersion?: string;
    agentsUsed?: string[];
    attempts?: number;
    totalCost?: number;
    totalLatency?: number;
  };
  createdAt: string;
  updatedAt?: string;
}

interface SearchFilters {
  exam?: string;
  oeCode?: string;
  difficulty?: string;
  type?: string;
  status?: string;
  search?: string;
  sourceType?: 'free_pdf' | 'standard'; // NQG-RQ-26 v2: filter main bank by question source
  page?: number;
  limit?: number;
}

export const questionsService = {
  // Generate questions (uses vector DB - may return mock data if not configured)
  async generateQuestions(data: GenerateQuestionsRequest) {
    const token = localStorage.getItem('accessToken');
    const response = await axios.post(`${API_URL}/generation/generate`, data, {
      headers: { Authorization: `Bearer ${token}` },
      timeout: 300000 // 5 minutes timeout for generation
    });
    return response.data;
  },

  // Generate questions directly from PDF file (bypasses vector DB)
  async generateFromPDF(file: File, data: Omit<GenerateQuestionsRequest, 'oeCode'> & { oeCode?: string }) {
    const token = localStorage.getItem('accessToken');
    const formData = new FormData();
    formData.append('pdf', file);
    formData.append('exam', data.exam);
    formData.append('count', data.count.toString());
    formData.append('questionType', data.questionType);
    if (data.difficulty) formData.append('difficulty', data.difficulty);
    if (data.customPrompt) formData.append('customPrompt', data.customPrompt);
    if (data.format) formData.append('format', data.format);
    if (data.oeCode) {
      formData.append('oeCode', data.oeCode); // CLIENT REQUIREMENT: Single OE code
    }

    const response = await axios.post(`${API_URL}/generation/from-pdf`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      },
      timeout: 300000 // 5 minutes timeout for generation
    });
    return response.data;
  },

  // Generate questions from text content (bypasses vector DB)
  async generateFromText(text: string, data: Omit<GenerateQuestionsRequest, 'oeCode'> & { oeCode?: string }) {
    const token = localStorage.getItem('accessToken');
    const response = await axios.post(`${API_URL}/generation/from-text`, {
      text,
      exam: data.exam,
      count: data.count,
      questionType: data.questionType,
      difficulty: data.difficulty,
      format: data.format,
      oeCode: data.oeCode, // CLIENT REQUIREMENT: Single OE code
      customPrompt: data.customPrompt
    }, {
      headers: { Authorization: `Bearer ${token}` },
      timeout: 300000 // 5 minutes timeout for generation
    });
    return response.data;
  },

  // Get all questions with filters
  async getQuestions(filters: SearchFilters = {}) {
    const token = localStorage.getItem('accessToken');
    const response = await axios.get(`${API_URL}/questions`, {
      headers: { Authorization: `Bearer ${token}` },
      params: filters
    });
    return response.data;
  },

  // Get single question
  async getQuestion(id: string) {
    const token = localStorage.getItem('accessToken');
    const response = await axios.get(`${API_URL}/questions/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    // Backend returns: { success: true, data: {...} }
    return response.data.data || response.data;
  },

  // Update question
  async updateQuestion(id: string, data: Partial<Question>) {
    const token = localStorage.getItem('accessToken');
    const response = await axios.patch(`${API_URL}/questions/${id}`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Delete question
  async deleteQuestion(id: string) {
    const token = localStorage.getItem('accessToken');
    const response = await axios.delete(`${API_URL}/questions/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Approve question
  async approveQuestion(id: string) {
    const token = localStorage.getItem('accessToken');
    const response = await axios.post(`${API_URL}/questions/${id}/approve`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Reject question
  async rejectQuestion(id: string, reason: string) {
    const token = localStorage.getItem('accessToken');
    const response = await axios.post(`${API_URL}/questions/${id}/reject`, { reason }, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Get question analytics
  async getAnalytics() {
    const token = localStorage.getItem('accessToken');
    const response = await axios.get(`${API_URL}/questions/analytics`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Delete all questions
  async deleteAllQuestions() {
    const token = localStorage.getItem('accessToken');
    const response = await axios.delete(`${API_URL}/questions/all`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Export questions to Excel
  async exportToExcel(filters: SearchFilters = {}): Promise<Blob> {
    const token = localStorage.getItem('accessToken');
    try {
      const response = await axios.get(`${API_URL}/questions/export/excel`, {
        headers: { Authorization: `Bearer ${token}` },
        params: filters,
        responseType: 'blob'
      });
      return response.data;
    } catch (error: any) {
      // When responseType is 'blob', error responses are also blobs
      // We need to parse them to get the actual error message
      if (error.response && error.response.data instanceof Blob) {
        const text = await error.response.data.text();
        try {
          const json = JSON.parse(text);
          error.response.data = json;
        } catch {
          // If not JSON, keep the text
          error.response.data = { error: text };
        }
      }
      throw error;
    }
  },

  // ===== MCQ FORMAT VALIDATION =====

  // Validate MCQ question against format constraints
  async validateMCQFormat(data: {
    stem: string;
    alternatives: Array<{ letter?: string; text: string; explanation?: string }>;
    explanation: string;
    exams?: string[];
    format: 'anbima' | 'short';
  }) {
    const token = localStorage.getItem('accessToken');
    const response = await axios.post(`${API_URL}/questions/mcq/validate`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Validate format is allowed for given exams
  async validateFormatForExams(data: {
    exams: string[];
    format: 'anbima' | 'short';
  }) {
    const token = localStorage.getItem('accessToken');
    const response = await axios.post(`${API_URL}/questions/mcq/validate-format-for-exams`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Get character diagnostics for MCQ
  async getCharacterDiagnostics(data: {
    stem: string;
    alternatives: Array<{ letter?: string; text: string }>;
    format: 'anbima' | 'short';
  }) {
    const token = localStorage.getItem('accessToken');
    const response = await axios.post(`${API_URL}/questions/mcq/character-diagnostics`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  /**
   * Refine an existing question using AI conversational prompts
   * Example: "make alternative B less obvious", "add distractor about green bonds"
   */
  async refineQuestion(question: any, refinementPrompt: string) {
    const token = localStorage.getItem('accessToken');
    const response = await axios.post(
      `${API_URL}/generation/refine`,
      {
        question,
        refinementPrompt
      },
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    );

    if (response.data.success && response.data.data.questions.length > 0) {
      return response.data.data.questions[0];
    } else {
      throw new Error('Refinement failed: No question returned');
    }
  },

  /**
   * Batch save multiple questions to database
   * Used after reviewing and refining generated questions
   */
  async batchSaveQuestions(questions: any[]) {
    const token = localStorage.getItem('accessToken');
    const response = await axios.post(
      `${API_URL}/questions/batch`,
      { questions },
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    );
    return response.data;
  }
};
